import { useEffect, useState, useMemo, useContext } from "react";
import "./style.css";
import { Breadcrumb, PopUpModal } from "../../Components/Atoms";
import PATHS from "../../Routes/paths";
import {
  CloudUploadIcon,
  FileIcon,
  CloseIcon,
  RoundCrossIcon,
  ChevronRightIcon,
  UploadingIcon,
} from "../../Assets/images/svgs";
import { Button, Spinner, Tooltip, useToast } from "@chakra-ui/react";
import { ReactTable } from "../../Components/Molecules";
import { ellipsisify } from "../../Library/helpers";
import { getAllVehMakes, getAllVehModels, mapDevToVeh } from "../../APIs";
import { AuthContext, StoreContext } from "../../Library/contexts";
import { useNavigate } from "react-router-dom";

const REQUIRED_HEADERS = [
  "Registration No",
  "Chassis No",
  "Vehicle Type",
  "Vehicle Make",
  "Vehicle Model",
  "Fuel Type",
  "Ajjas IMEI",
];

const VEHICLE_TYPE_OPTIONS = [
  { idx: 1, name: "Two wheeler" },
  { idx: 2, name: "Four wheeler" },
  { idx: 3, name: "Auto rickshaw" },
  { idx: 4, name: "Heavy vehicle" },
];

const FUEL_TYPE_OPTIONS = [
  { idx: 1, name: "Petrol" },
  { idx: 2, name: "Diesel" },
  { idx: 3, name: "Electric" },
  { idx: 4, name: "CNG" },
  { idx: 5, name: "CNG+Petrol" },
];

const MAP_DEVS_LIMIT = 100;

export default function BulkMapDevToVeh() {
  const [phase, setPhase] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [progress, setProgress] = useState(0);
  const [showModalPreview, setShowModalPreview] = useState(false);
  const [popupModalData, setPopupModalData] = useState(null);
  const [allVehicleMakes, setAllVehicleMakes] = useState([]);
  const [allVehicleModels, setAllVehicleModels] = useState([]);
  const Toast = useToast();
  const navigate = useNavigate();

  const { profile } = useContext(AuthContext);
  const { userVehicles, setUserVehicles } = useContext(StoreContext);

  const breadcrumbPaths = [
    { idx: 1, name: "Live Tracking", link: PATHS.LIVE_TRACKING },
    { idx: 2, name: "Bulk Map Device", isCurrentPage: true },
  ];

  const stripBOM = (str) => (str && str.charCodeAt(0) === 0xfeff ? str.slice(1) : str);

  const parseCsvLine = (line) => {
    const result = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
        continue;
      }
      if (ch === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (ch === "," && !inQuotes) {
        result.push(cur);
        cur = "";
        continue;
      }
      cur += ch;
    }
    result.push(cur);
    return result.map((f) => (f !== undefined && f !== null ? f.trim() : ""));
  };

  const validateHeadersExact = (headersArr) => {
    if (!Array.isArray(headersArr)) return false;
    if (headersArr.length < REQUIRED_HEADERS.length) return false;
    for (let i = 0; i < REQUIRED_HEADERS.length; i++) {
      if ((headersArr[i] || "").trim() !== REQUIRED_HEADERS[i]) return false;
    }
    return true;
  };

  const fetchMakeModels = async () => {
    try {
      const [makes, models] = await Promise.all([
        new Promise((resolve, reject) =>
          getAllVehMakes((err, res) => (err ? reject(err) : resolve(res)))
        ),
        new Promise((resolve, reject) =>
          getAllVehModels((err, res) => (err ? reject(err) : resolve(res)))
        ),
      ]);
      setAllVehicleMakes(makes);
      setAllVehicleModels(models);
      return { vehMakes: makes, vehModels: models };
    } catch (err) {
      console.error("Failed to fetch makes/models:", err);
      return { vehMakes: [], vehModels: [] };
    }
  };

  const parseCSVText = async (text) => {
    if (!text || !text.trim()) {
      setErrorMsg("Uploaded file seems empty!");
      setPhase("error");
      return;
    }

    const cleanedText = stripBOM(text).replace(/\r/g, "");
    const lines = cleanedText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) {
      setErrorMsg("Uploaded file seems empty!");
      setPhase("error");
      return;
    }

    if ((lines.length - 1) > MAP_DEVS_LIMIT) {
      setErrorMsg(`You can only map 100 devices at once! Your file has ${lines.length - 1} lines.`);
      setPhase("error");
      return;
    }

    const headerLine = lines[0];
    const headers = parseCsvLine(headerLine);
    if (!validateHeadersExact(headers)) {
      setErrorMsg("Invalid headers. Please use the provided template.");
      setPhase("error");
      return;
    }

    const parsedRows = [];
    for (let i = 1; i < lines.length; i++) {
      const fields = parseCsvLine(lines[i]);
      const obj = {};
      for (let h = 0; h < REQUIRED_HEADERS.length; h++) {
        obj[REQUIRED_HEADERS[h]] = fields[h] !== undefined ? fields[h] : "";
      }
      parsedRows.push(obj);
    }

    if (!parsedRows.length) {
      setErrorMsg("No rows found in file.");
      setPhase("error");
      return;
    }

    const hasEmpty = parsedRows.some((r) =>
      ["Registration No", "Vehicle Type", "Vehicle Make", "Vehicle Model", "Fuel Type", "Ajjas IMEI"].some((h) => !r[h] || r[h].toString().trim() === "")
    );
    if (hasEmpty) {
      setErrorMsg("Some rows contain empty fields.");
      setPhase("error");
      return;
    }

    const { vehMakes, vehModels } =
      allVehicleMakes.length && allVehicleModels.length
        ? { vehMakes: allVehicleMakes, vehModels: allVehicleModels }
        : await fetchMakeModels();

    const validVehicleTypes = VEHICLE_TYPE_OPTIONS.map((v) => v.name.toLowerCase());
    const validFuelTypes = FUEL_TYPE_OPTIONS.map((v) => v.name.toLowerCase());

    let imeiCountMap = {};
    parsedRows.forEach(row => {
      let imei = row["Ajjas IMEI"];
      if (!imeiCountMap[imei]) imeiCountMap[imei] = 1;
      else imeiCountMap[imei] += 1;
    });

    const invalid = [];
    let anyInvalid = false;
    const processed = parsedRows.map((r, idx) => {
      const vehType = r["Vehicle Type"]?.toLowerCase();
      const fuelType = r["Fuel Type"]?.toLowerCase();
      const makeNm = r["Vehicle Make"]?.toLowerCase();
      const modelNm = r["Vehicle Model"]?.toLowerCase();
      const imei = r["Ajjas IMEI"].trim();

      const invalidVeh = !validVehicleTypes.includes(vehType);
      const invalidFuel = !validFuelTypes.includes(fuelType);
      const invalidImei = !!(imeiCountMap[imei] && imeiCountMap[imei] > 1);

      const vehTypeObj = VEHICLE_TYPE_OPTIONS.find(
        (v) => v.name.toLowerCase() === vehType
      );
      const fuelTypeObj = FUEL_TYPE_OPTIONS.find(
        (f) => f.name.toLowerCase() === fuelType
      );

      let invalidMake = false;
      let invalidModel = false;
      let makeId = null;
      let modelId = null;

      if (vehTypeObj) {
        const makeSet = vehMakes.find((m) => m.typ === vehTypeObj.idx)?.d || [];
        const foundMake = makeSet.find(
          (m) => m.nm.toLowerCase() === makeNm
        );

        if (!foundMake) {
          invalidMake = true;
        } else {
          makeId = foundMake.idx;
          const modelSet = vehModels.find((m) => m.typ === vehTypeObj.idx)?.d || [];
          const foundModel = modelSet.find(
            (mm) =>
              mm.nm.toLowerCase() === modelNm &&
              Object.values(mm).some((v) => v === foundMake.idx)
          );
          if (!foundModel) {
            invalidModel = true;
          } else {
            modelId = foundModel.idx;
          }
        }
      } else {
        invalidMake = true;
        invalidModel = true;
      }

      const rowObj = {
        ...r,
        invalidVeh: invalidVeh,
        invalidFuel: invalidFuel,
        invalidMake: invalidMake,
        invalidModel: invalidModel,
        invalidImei: invalidImei,
        rowIndex: idx + 2,
        typ: vehTypeObj?.idx || null,
        ftyp: fuelTypeObj?.idx || null,
        mk: makeId,
        md: modelId,
      };

      if (!anyInvalid || !anyInvalid.rowIndex) anyInvalid = (invalidVeh || invalidFuel || invalidMake || invalidModel || invalidImei) ? rowObj : false;

      if (anyInvalid) invalid.push(rowObj);
      return rowObj;
    });

    if (invalid.length) {
      setInvalidRows(invalid);
      setRows([]);
      setErrorMsg(`Some rows contain invalid ${(anyInvalid.invalidVeh && "Vehicle Types") || (anyInvalid.invalidFuel && "Fuel Types") || (anyInvalid.invalidMake && "Vehicle Makes") || (anyInvalid.invalidModel && "Vehicle Models") || (anyInvalid.invalidImei && "or duplicate Ajjas IMEIs")
        }`);
      setPhase("preview");
      return;
    }

    setInvalidRows([]);
    setRows(processed);
    setErrorMsg("");
    setPhase("uploading");
  };

  const handleFile = (file) => {
    if (!file) return;
    if (file.type !== "text/csv" && !file.name?.toLowerCase().endsWith(".csv")) {
      setErrorMsg("Invalid file type. Only CSV allowed.");
      setPhase("error");
      return;
    }
    setUploadedFile(file);

    const reader = new FileReader();
    reader.onload = async (e) => await parseCSVText(e.target.result);
    reader.readAsText(file);
  };

  const handleFileInput = (e) => {
    const f = e.target.files?.[0];
    handleFile(f);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  };

  useEffect(() => {
    let timer;
    if (phase === "uploading") {
      const baseInterval = 90;
      const sizeFactor = Math.min(6, Math.max(1, (uploadedFile?.size || 0) / (1024 * 1024)));
      let val = 0;
      timer = setInterval(() => {
        val += Math.max(8, Math.round(6 * sizeFactor));
        if (val >= 100) {
          val = 100;
          setProgress(100);
          clearInterval(timer);
          setTimeout(() => setPhase("preview"), 220);
          return;
        }
        setProgress(val);
      }, baseInterval);
    } else {
      setProgress(0);
    }
    return () => clearInterval(timer);
  }, [phase, uploadedFile]);

  const removeUploadedFile = () => {
    setPhase("idle");
    setUploadedFile(null);
    setRows([]);
    setInvalidRows([]);
    setErrorMsg("");
    setProgress(0);
  };

  const columns = useMemo(
    () =>
      REQUIRED_HEADERS.map((h) => ({
        Header: h,
        accessor: h,
        Cell: ({ row, value }) => {
          const displayValue = value ?? "--";
          const shouldTruncate = displayValue.length > 15;
          const isImeiCol = h.toLowerCase().includes("imei");

          const handleCopy = (e) => {
            e.stopPropagation();
            if (value) {
              navigator.clipboard.writeText(value);
              Toast({
                title: "Copied!",
                description: `${h} copied to clipboard.`,
                status: "success",
                duration: 2000,
                isClosable: true,
                position: "bottom",
              });
            }
          };

          const content = shouldTruncate ? (
            <Tooltip label={displayValue} hasArrow placement="top" bg="gray.700">
              <span>{ellipsisify(displayValue, 12)}</span>
            </Tooltip>
          ) : (
            <span>{displayValue}</span>
          );

          const invalidClass =
            row.original.invalidVeh && h === "Vehicle Type"
              ? "invalid-cell"
              : row.original.invalidFuel && h === "Fuel Type"
                ? "invalid-cell"
                : row.original.invalidMake && h === "Vehicle Make"
                  ? "invalid-cell"
                  : row.original.invalidModel && h === "Vehicle Model"
                    ? "invalid-cell"
                    : row.original.invalidImei && h === "Ajjas IMEI"
                      ? "invalid-cell"
                      : "";

          return (
            <span
              onClick={isImeiCol ? handleCopy : undefined}
              className={invalidClass}
              style={{
                cursor: isImeiCol ? "pointer" : "default",
              }}
            >
              {content}
            </span>
          );
        },
      })),
    [Toast]
  );

  const downloadTemplate = () => {
    const csv = `${REQUIRED_HEADERS.join(",")}\n`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "VehicleListTemplate.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleMappingSuccess = (mappingDets) => {
    setUserVehicles([...userVehicles, ...mappingDets]);
    navigate(`${PATHS.LIVE_TRACKING}`);
  }

  const handleBulkMapping = () => {
    if (!profile || !profile.idx || !profile.mob) return;
    setPopupModalData({
      open: true,
      modalMsg: <Spinner className="veh-mapping-loader" size={"lg"} />,
    });

    let payload = {
      mapOnExistingVeh: false,
      mappingDetl: []
    }
    rows.forEach((veh, index) => {
      let details = {
        imei: veh["Ajjas IMEI"],
        reg: veh["Registration No"],
        vtyp: veh["typ"],
        ftyp: veh["ftyp"],
        mk: veh["mk"],
        md: veh["md"],
        vmk: veh["Vehicle Make"],
        mo: veh["Vehicle Model"],
        idx: (index + 1),
        prod_type: 1,
      }
      if (veh["Chassis No"]) details.chNum = veh["Chassis No"];

      payload.mappingDetl.push(details);
    });

    if (!payload.mappingDetl.length) return;

    mapDevToVeh(payload, (err, res) => {
      if (err) {
        console.error("Mapping failed:", err);
        setPopupModalData(null);
        Toast({ title: err, status: "error", isClosable: true, position: "bottom", });
      } else {
        setPopupModalData({
          open: true,
          modalMsg: (
            <div className="vehs-added-uploading-icon">
              <UploadingIcon />
              <p>{rows.length} Vehicles added to your panel</p>
            </div>
          ),
          confirmBtn: "Take me there",
          onConfirm: () => handleMappingSuccess(payload.mappingDetl),
          onModalClose: () => handleMappingSuccess(payload.mappingDetl),
        });
      }
    });
  };

  const confirmAddVehicles = () => {
    setPopupModalData({
      open: true,
      modalMsg: `You are adding ${rows.length} vehicles. Are you sure you want to continue?`,
      closeBtn: "Cancel",
      confirmBtn: "Continue",
      onConfirm: handleBulkMapping,
      onModalClose: () => setPopupModalData(null),
    });
  };

  return (
    <section className="panel-main bulk-map-dev">
      <Breadcrumb paths={breadcrumbPaths} />
      <div className="upload-vehicle-list">
        <p className="operation-field-label">Upload Vehicle List</p>

        <div className="uploader-container" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
          {(phase === "idle" || phase === "error") && (
            <>
              <p className="uploader-title">
                Don't have the template?
                <span className="downloadTemplate" onClick={downloadTemplate}>
                  Click Here!
                </span>
              </p>
              <p className="uploader-note">*Use the provided template for best results</p>

              <label className={`upload-box ${phase === "error" ? "show-overlay" : ""}`}>
                <CloudUploadIcon className="cloud-upload-icon-veh-list" />
                <p className={`uploader-hint ${phase === "error" ? "error" : ""}`}>
                  {phase === "error" ? errorMsg : "Drag file here or Browse for file upload"}
                </p>
                <input type="file" accept=".csv" className="upload-file-input" onChange={handleFileInput} />
              </label>
            </>
          )}

          {phase === "uploading" && (
            <div className="uploaded-file-box">
              <FileIcon className="uploaded-file-icon" />
              <div className="loader-container">
                <Tooltip label={uploadedFile?.name?.length > 30 ? uploadedFile.name : ""}>
                  <p className="file-name">{ellipsisify(uploadedFile?.name || "Upload.csv", 30)}</p>
                </Tooltip>
                <div className="loader-bar">
                  <div className="progress-bar" style={{ width: `${progress}%` }} />
                </div>
                <div className="file-details">
                  <p className="file-size">
                    {uploadedFile?.size ? `${(uploadedFile.size / (1024 * 1024)).toFixed(3)} MB` : ""}
                  </p>
                  <p className="file-uploaded-hint">
                    {progress < 100 ? "Processing..." : "Preparing preview..."}
                  </p>
                </div>
              </div>
              <CloseIcon className="uploaded-file-cross-icon" onClick={removeUploadedFile} />
            </div>
          )}

          {phase === "preview" && (
            <>
              <div className="uploaded-file-box">
                <FileIcon className="uploaded-file-icon" />
                <div className="loader-container">
                  <Tooltip label={uploadedFile?.name?.length > 30 ? uploadedFile.name : ""}>
                    <p className="file-name">{ellipsisify(uploadedFile?.name || "Upload.csv", 30)}</p>
                  </Tooltip>
                  <div className="loader-bar">
                    <div className="progress-bar" style={{ width: "100%" }} />
                  </div>
                  <div className="file-details">
                    <p className="file-size">
                      {uploadedFile?.size
                        ? `${(uploadedFile.size / (1024 * 1024)).toFixed(3)} MB`
                        : ""}
                    </p>
                    <p className="file-uploaded-hint">
                      {invalidRows.length > 0 ? "Invalid rows detected" : "Uploaded"}
                    </p>
                  </div>
                </div>
                <CloseIcon className="uploaded-file-cross-icon" onClick={removeUploadedFile} />
              </div>

              <div className="uploaded-data-container">
                <ReactTable
                  columns={columns}
                  data={invalidRows.length ? invalidRows.slice(0, 3) : rows.slice(0, 3)}
                  pageSize={3}
                />
                {(invalidRows.length > 3 || rows.length > 3) && (
                  <div className="preview-btn-container">
                    <p className="copy-imei-text">Click any Ajjas IMEI to copy it</p>
                    <Button
                      onClick={() => setShowModalPreview(true)}
                      variant={"link"}
                      className="file-preview-button"
                    >
                      Preview complete file <ChevronRightIcon />
                    </Button>
                  </div>
                )}
              </div>
              {errorMsg && <p className="invalid-msg">{errorMsg}</p>}
            </>
          )}
        </div>

        {phase === "preview" && !invalidRows.length && rows.length > 0 && (
          <Button className="bulk-add-vehs-btn" onClick={confirmAddVehicles}>
            Add Vehicles
          </Button>
        )}
      </div>

      {showModalPreview && (
        <div className="file-previewModal-overlay">
          <div className="modal-cross-icon-container">
            <RoundCrossIcon
              onClick={() => setShowModalPreview(false)}
              className="modal-cross-icon"
            />
          </div>
          <div className="map-dev-prev-file-table-container">
            <ReactTable
              columns={columns}
              data={invalidRows.length ? invalidRows : rows}
              pageSize={10000}
              showSearch={true}
            />
          </div>
        </div>
      )}

      <PopUpModal {...popupModalData} />
    </section>
  );
}
